/**
 * SCR-007 — Rooms list (admin + manager).
 * Admin: sees all rooms with branch filter.
 * Manager: branch filter hidden/locked; API scopes data server-side.
 */
import { createFileRoute } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@client/components/data-table';
import { RoomForm, type RoomFormValues } from '@client/components/forms/room-form';
import { useBranches, isApiError } from '@client/lib/queries/use-branches';
import {
  type Room,
  roomKeys,
  useCreateRoom,
  useRooms,
} from '@client/lib/queries/use-rooms';
import { useBranchFilter } from '@client/store/branch-filter';
import { api } from '@client/lib/api-client';

export const Route = createFileRoute('/admin/rooms/')({
  component: RoomsListPage,
});

function RoomsListPage() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { role?: string } };
  };
  const role = session?.user?.role ?? '';
  const qc = useQueryClient();

  const { selectedBranchId } = useBranchFilter();
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRoom, setEditRoom] = useState<Room | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effective branch filter: admin uses switcher or manual; manager locked server-side
  const effectiveBranch =
    role === 'manager'
      ? undefined
      : branchFilter !== 'all'
        ? branchFilter
        : selectedBranchId ?? undefined;

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    branchId: effectiveBranch,
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
  };

  const { data, isLoading, isError, refetch } = useRooms(params);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const branches = branchesData?.items ?? [];
  const createRoom = useCreateRoom();

  async function handleSubmit(values: RoomFormValues) {
    setFormError(null);
    setIsSubmitting(true);
    try {
      if (editRoom) {
        // Update via direct API call to avoid hook-in-callback violation
        await api.patch(`/api/rooms/${editRoom.id}`, values);
        await qc.invalidateQueries({ queryKey: roomKeys.all });
        toast.success('Đã cập nhật phòng.');
      } else {
        await createRoom.mutateAsync(values);
        toast.success('Đã thêm phòng.');
      }
      setDialogOpen(false);
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setFormError('Tên phòng đã tồn tại trong cơ sở này.');
      } else {
        setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Xóa phòng này?')) return;
    try {
      await api.delete(`/api/rooms/${id}`);
      await qc.invalidateQueries({ queryKey: roomKeys.all });
      toast.success('Đã xóa phòng.');
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        toast.error('Phòng đang được dùng. Hãy tắt phòng thay vì xóa.');
      } else {
        toast.error('Không thể xóa phòng.');
      }
    }
  }

  const columns: ColumnDef<Room>[] = [
    {
      accessorKey: 'name',
      header: 'Phòng',
      cell: ({ row }) => (
        <span className="font-mono text-sm font-medium">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'branchName',
      header: 'Cơ sở',
      cell: ({ row }) => row.original.branchName ?? '—',
    },
    {
      accessorKey: 'capacity',
      header: 'Sức chứa',
    },
    {
      accessorKey: 'isActive',
      header: 'Trạng thái',
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge className="bg-brand-green-100 text-brand-green-700 border-0 text-xs">Đang dùng</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">Đã tắt</Badge>
        ),
    },
    {
      accessorKey: 'note',
      header: 'Ghi chú',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">{row.original.note ?? '—'}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                setEditRoom(row.original);
                setFormError(null);
                setDialogOpen(true);
              }}
            >
              <Pencil className="h-4 w-4 mr-2" /> Sửa
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => void handleDelete(row.original.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Xóa
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Phòng học</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={() => { setEditRoom(null); setFormError(null); setDialogOpen(true); }}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Thêm phòng
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage="Chưa có phòng học nào."
        searchPlaceholder="Tìm tên phòng..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={data ? { page, pageSize: PAGE_SIZE, total: data.total } : undefined}
        onPageChange={setPage}
        filterSlot={
          <div className="flex flex-wrap gap-2">
            {/* Branch filter — admin only */}
            {role === 'admin' && (
              <Select value={branchFilter} onValueChange={(v) => { setBranchFilter(v); setPage(1); }}>
                <SelectTrigger className="h-10 w-[160px]">
                  <SelectValue placeholder="Cơ sở" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả cơ sở</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* Active filter */}
            <Select value={isActiveFilter} onValueChange={(v) => { setIsActiveFilter(v); setPage(1); }}>
              <SelectTrigger className="h-10 w-[140px]">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                <SelectItem value="true">Đang dùng</SelectItem>
                <SelectItem value="false">Đã tắt</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editRoom ? 'Sửa phòng' : 'Thêm phòng'}</DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <RoomForm
            isEdit={!!editRoom}
            isSubmitting={isSubmitting}
            branches={role === 'admin' ? branches : undefined}
            defaultValues={
              editRoom
                ? {
                    branchId: editRoom.branchId,
                    name: editRoom.name,
                    capacity: editRoom.capacity,
                    isActive: editRoom.isActive,
                    note: editRoom.note ?? '',
                  }
                : undefined
            }
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
